// Audit log kampanye promo (mig 5990, audit A8) — helper murni: pemetaan baris
// `promo_campaign_audit_log` + teks "kolom: dari → ke" untuk seksi Riwayat di
// editor. Cermin `voucherAdmin.ts` (voucher_audit_log 5610); sengaja modul
// sendiri supaya label kolomnya milik kampanye, bukan voucher.

export type PromoAuditAction = "create" | "update" | "status" | "delete";

export type PromoAuditChange = { from: unknown; to: unknown };

export type PromoAuditEntry = {
  id: string;
  campaignId: string | null;
  campaignName: string;
  actorId: string | null;
  actorName: string;
  action: PromoAuditAction;
  changes: Record<string, PromoAuditChange>;
  createdAt: string | null;
};

function str(raw: unknown): string | null {
  return typeof raw === "string" && raw !== "" ? raw : null;
}

function asRecord(raw: unknown): Record<string, unknown> | null {
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null;
}

export function mapPromoAuditRows(raw: unknown): PromoAuditEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: PromoAuditEntry[] = [];
  for (const item of raw) {
    const r = asRecord(item);
    const id = r ? str(r.id) : null;
    if (!r || !id) continue;
    const action: PromoAuditAction =
      r.action === "create" || r.action === "status" || r.action === "delete" ? r.action : "update";
    const changes: Record<string, PromoAuditChange> = {};
    const rawChanges = asRecord(r.changes);
    if (rawChanges) {
      for (const [key, value] of Object.entries(rawChanges)) {
        const c = asRecord(value);
        changes[key] = c ? { from: c.from ?? null, to: c.to ?? null } : { from: null, to: value };
      }
    }
    out.push({
      id,
      campaignId: str(r.campaign_id),
      campaignName: typeof r.campaign_name === "string" ? r.campaign_name : "",
      actorId: str(r.actor_id),
      actorName: typeof r.actor_name === "string" && r.actor_name ? r.actor_name : "admin",
      action,
      changes,
      createdAt: str(r.created_at)
    });
  }
  return out;
}

export function promoAuditActionLabel(action: PromoAuditAction): string {
  switch (action) {
    case "create":
      return "Dibuat";
    case "status":
      return "Status diubah";
    case "delete":
      return "Dihapus";
    default:
      return "Diubah";
  }
}

/** Label kolom `promo_campaigns` (+ potret target & aturan ukuran) di Riwayat. */
const PROMO_AUDIT_FIELD_LABELS: Record<string, string> = {
  name: "Nama",
  description: "Deskripsi",
  is_active: "Aktif",
  starts_at: "Mulai",
  ends_at: "Berakhir",
  discount_mode: "Jenis potongan",
  discount_value: "Besar potongan",
  product_type_scope: "Cakupan tipe produk",
  target_type: "Jenis target",
  targets: "Target",
  size_rules_exclusive: "Hanya ukuran beraturan",
  sizeRules: "Aturan ukuran",
  level_slots: "Siapa yang dapat promo",
  level_policy: "Cara berlaku"
};

const LEVEL_POLICY_LABEL: Record<string, string> = {
  replace: "Harga promo final",
  stack: "Diskon harga coret"
};

function formatPromoAuditValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "ya" : "tidak";
  if (field === "level_policy" && typeof value === "string") return LEVEL_POLICY_LABEL[value] ?? value;
  if (field === "level_slots" && Array.isArray(value)) {
    if (!value.length) return "semua pembeli";
    return value.map((v) => (Number(v) === 0 ? "pelanggan umum" : `level ${String(v)}`)).join(", ");
  }
  if (Array.isArray(value)) return value.length ? value.map(String).join(", ") : "—";
  if ((field === "starts_at" || field === "ends_at") && typeof value === "string") {
    const d = new Date(value);
    return Number.isFinite(d.getTime())
      ? d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
      : value;
  }
  return typeof value === "string" ? value : JSON.stringify(value);
}

export type PromoAuditChangeView = { field: string; label: string; from: string; to: string };

/** Baris "kolom: dari → ke" untuk seksi Riwayat; urut label. */
export function describePromoAuditChanges(entry: Pick<PromoAuditEntry, "changes">): PromoAuditChangeView[] {
  return Object.entries(entry.changes)
    .map(([field, c]) => ({
      field,
      label: PROMO_AUDIT_FIELD_LABELS[field] ?? field,
      from: formatPromoAuditValue(field, c.from),
      to: formatPromoAuditValue(field, c.to)
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "id"));
}
